import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, CircularProgress, Button, Chip, Stack, Divider } from '@mui/material';
import { CheckCircle, AccessTime, LocalDining, DeliveryDining, DoneAll, Print } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { subscribeToOrder } from '../lib/orders';
import { useLang } from '../i18n/LanguageProvider';
import { useCart } from '../store/cart';
import type { SellerProfile } from '../lib/zatca';

interface OrderItemRow {
  id: string;
  menu_item_snapshot: any;
  quantity: number;
  unit_price: number;
  line_total: number;
  notes: string | null;
}

interface OrderRow {
  id: string;
  tenant_id: string;
  order_number: string | null;
  status: string;
  subtotal: number | null;
  tax_total: number | null;
  grand_total: number | null;
  type: string | null;
  customer_snapshot: any;
  tax_meta: { zatca_qr?: string } | null;
  notes: string | null;
  created_at: string;
}

interface ExtendedSeller extends SellerProfile {
  display_name?: string;
  address?: string;
  phone?: string;
  email?: string;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending:    <AccessTime sx={{ fontSize: 48, color: '#D4AF37' }} />,
  confirmed:  <CheckCircle sx={{ fontSize: 48, color: '#00C896' }} />,
  preparing:  <LocalDining sx={{ fontSize: 48, color: '#00C896' }} />,
  ready:      <CheckCircle sx={{ fontSize: 48, color: '#00C896' }} />,
  out_for_delivery: <DeliveryDining sx={{ fontSize: 48, color: '#00C896' }} />,
  delivered:  <DoneAll sx={{ fontSize: 48, color: '#00C896' }} />,
  completed:  <DoneAll sx={{ fontSize: 48, color: '#00C896' }} />,
};

export const OrderTracking: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLang();
  const { clear } = useCart();
  const [order, setOrder] = useState<OrderRow | null>(null);
  const [items, setItems] = useState<OrderItemRow[]>([]);
  const [seller, setSeller] = useState<ExtendedSeller | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    (async () => {
      const [orderRes, itemsRes] = await Promise.all([
        supabase.from('orders').select('*').eq('id', id).maybeSingle(),
        supabase.from('order_items').select('id, menu_item_snapshot, quantity, unit_price, line_total, notes').eq('order_id', id),
      ]);
      if (!alive) return;
      const row = (orderRes.data as OrderRow) ?? null;
      setOrder(row);
      setItems((itemsRes.data as OrderItemRow[]) || []);
      setLoading(false);

      if (row?.tenant_id) {
        const { data: t } = await supabase.rpc('public_tenant_info', { p_tenant_id: row.tenant_id });
        const tenant = Array.isArray(t) ? t[0] : t;
        if (alive && tenant) {
          setSeller({
            name: tenant.legal_name || tenant.display_name || 'Saheeh',
            display_name: tenant.display_name || tenant.legal_name,
            address: tenant.address || '',
            phone: tenant.phone || '',
            email: tenant.email || '',
            vatNumber: tenant.vat_number || '',
            region: tenant.region || null,
          });
        }
      }
    })();
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const unsub = subscribeToOrder(id, (newStatus) => {
      setOrder((prev) => (prev ? { ...prev, status: newStatus } : prev));
    });
    return unsub;
  }, [id]);

  const startOver = () => {
    clear();
    navigate('/');
  };

  const handlePrintInvoice = async () => {
    if (!order || !seller) return;
    const { openInvoiceWindow } = await import('../lib/invoiceHtml');
    await openInvoiceWindow({
      orderNumber: order.order_number || order.id.slice(0, 8),
      createdAtIso: order.created_at,
      customerName:    order.customer_snapshot?.name || '',
      customerPhone:   order.customer_snapshot?.phone || '',
      customerAddress: order.customer_snapshot?.address || '',
      type: order.type || 'order',
      items: items.map((it) => ({
        name: it.menu_item_snapshot?.name || 'Item',
        quantity: it.quantity || 1,
        unitPrice: Number(it.unit_price) || 0,
        lineTotal: Number(it.line_total) || (Number(it.unit_price) * (it.quantity || 1)),
        notes: it.notes,
      })),
      subtotal:   Number(order.subtotal)    || 0,
      taxTotal:   Number(order.tax_total)   || 0,
      grandTotal: Number(order.grand_total) || 0,
      currency: seller.region === 'SA' ? 'SAR' : 'EUR',
      seller: {
        display_name: seller.display_name || seller.name,
        legal_name: seller.name,
        address: seller.address,
        phone: seller.phone,
        email: seller.email,
        vat_number: seller.vatNumber,
      },
      zatcaQrBase64: order.tax_meta?.zatca_qr || null,
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#00C896' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 4, pb: 8, textAlign: 'center' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
        {order ? STATUS_ICONS[order.status] || <AccessTime sx={{ fontSize: 48, color: '#D4AF37' }} /> : null}

        <Typography sx={{ fontSize: '28px', fontWeight: 800, color: '#fff' }}>
          {t('track.title')}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
          {t('track.subtitle')}
        </Typography>

        {order && (
          <Box
            sx={{
              backgroundColor: 'rgba(10,37,64,0.6)',
              border: '1px solid rgba(0,200,150,0.2)',
              borderRadius: 2.5,
              p: 3,
              width: '100%',
              mt: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', letterSpacing: '1px', mb: 0.5 }}>
                {t('track.order_number').toUpperCase()}
              </Typography>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '22px' }}>
                #{order.order_number || order.id.slice(0, 8)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', letterSpacing: '1px', mb: 0.5 }}>
                {t('track.status').toUpperCase()}
              </Typography>
              <Chip
                label={order.status.replace('_', ' ').toUpperCase()}
                sx={{
                  backgroundColor: order.status === 'pending' ? 'rgba(212,175,55,0.15)' : 'rgba(0,200,150,0.15)',
                  color: order.status === 'pending' ? '#D4AF37' : '#00C896',
                  fontWeight: 700,
                  border: `1px solid ${order.status === 'pending' ? 'rgba(212,175,55,0.3)' : 'rgba(0,200,150,0.3)'}`,
                }}
              />
            </Box>
            {order.grand_total != null && (
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', letterSpacing: '1px', mb: 0.5 }}>
                  TOTAL
                </Typography>
                <Typography sx={{ color: '#00C896', fontWeight: 700, fontSize: '22px' }}>
                  {Number(order.grand_total).toFixed(2)} {seller?.region === 'SA' ? 'SAR' : 'EUR'}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ───── Order items ───── (always shown; placeholder while loading) */}
        {order && (
          <Box
            sx={{
              backgroundColor: 'rgba(10,37,64,0.6)',
              border: '1px solid rgba(0,200,150,0.2)',
              borderRadius: 2.5,
              p: 3,
              width: '100%',
              mt: 2,
              textAlign: 'left',
            }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', letterSpacing: '1px', mb: 1.5 }}>
              YOUR ORDER
            </Typography>

            {items.length === 0 ? (
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontStyle: 'italic', textAlign: 'center', py: 1.5 }}>
                Loading order details…
              </Typography>
            ) : (
              <Stack divider={<Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />} spacing={1.25}>
                {items.map((it) => {
                  const name = it.menu_item_snapshot?.name || 'Item';
                  const lineTotal = Number(it.line_total) || (Number(it.unit_price) * (it.quantity || 1));
                  return (
                    <Box key={it.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Box>
                        <Typography sx={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>
                          {it.quantity || 1}× {name}
                        </Typography>
                        {it.notes && (
                          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontStyle: 'italic', mt: 0.25 }}>
                            {it.notes}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ color: '#00C896', fontSize: '14px', fontWeight: 700, flexShrink: 0 }}>
                        {lineTotal.toFixed(2)} {seller?.region === 'SA' ? 'SAR' : 'EUR'}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}

            {order.tax_total != null && order.subtotal != null && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed rgba(255,255,255,0.15)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px' }}>
                    Subtotal (excl. VAT)
                  </Typography>
                  <Typography sx={{ color: '#fff', fontSize: '13px' }}>
                    {Number(order.subtotal).toFixed(2)} {seller?.region === 'SA' ? 'SAR' : 'EUR'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px' }}>
                    VAT 15%
                  </Typography>
                  <Typography sx={{ color: '#fff', fontSize: '13px' }}>
                    {Number(order.tax_total).toFixed(2)} {seller?.region === 'SA' ? 'SAR' : 'EUR'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* ZATCA QR is intentionally NOT shown on this page — it only
            appears on the printed receipt (Print Invoice button below).
            Restaurants display the QR on the printed/PDF invoice, not on
            the live tracking screen. */}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3, width: '100%' }}>
          <Button
            onClick={handlePrintInvoice}
            disabled={!order}
            startIcon={<Print />}
            sx={{
              flex: 1,
              py: 1.25,
              px: 3,
              backgroundColor: 'rgba(255,255,255,0.08)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.2)',
              fontWeight: 600,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
            }}
          >
            Print Invoice
          </Button>
          <Button
            onClick={startOver}
            sx={{
              flex: 1,
              py: 1.25,
              px: 3,
              background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
              color: '#0A2540',
              fontWeight: 700,
              '&:hover': { background: 'linear-gradient(135deg, #1AD4A6 0%, #00B387 100%)' },
            }}
          >
            {t('track.new_order')}
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};
